import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getSystemActivities, Activity } from '../services/activityService';

/**
 * System Activity Feed Component
 *
 * Displays recent activities across all companies for Platform Admins.
 * Features:
 * - Shows last 20 activities system-wide
 * - Company name prominently displayed
 * - Auto-refreshes every 30 seconds
 * - Scrollable container with max height
 */

export function SystemActivityFeed(): React.ReactElement {
  const { token } = useAuth();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load activities
  const loadActivities = async () => {
    if (!token) return;

    try {
      setError(null);
      const data = await getSystemActivities({ limit: 20 }, token);
      setActivities(data);
    } catch (err) {
      console.error('Error loading system activities:', err);
      setError(err instanceof Error ? err.message : 'Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadActivities();
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Format relative time
  const formatRelativeTime = (timestamp: string): string => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffMs = now.getTime() - activityTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return activityTime.toLocaleDateString();
  };

  // Get action type badge color
  const getActionTypeColor = (actionType: string): string => {
    if (actionType.includes('create')) return 'bg-green-100 text-green-800';
    if (actionType.includes('update') || actionType.includes('edit')) return 'bg-blue-100 text-blue-800';
    if (actionType.includes('delete') || actionType.includes('remove')) return 'bg-red-100 text-red-800';
    if (actionType.includes('login') || actionType.includes('auth')) return 'bg-purple-100 text-purple-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Get activity icon
  const getActivityIcon = (actionType: string): React.ReactElement => {
    if (actionType.includes('create')) {
      return (
        <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    }
    if (actionType.includes('update') || actionType.includes('edit')) {
      return (
        <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      );
    }
    if (actionType.includes('delete') || actionType.includes('remove')) {
      return (
        <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      );
    }
    if (actionType.includes('login') || actionType.includes('auth')) {
      return (
        <svg className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
        </svg>
      );
    }
    return (
      <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-5 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">System Activity Feed</h3>
        <p className="mt-1 text-sm text-gray-500">Recent activities across all companies</p>
      </div>

      {error && (
        <div className="px-6 py-4 bg-red-50 border-b border-red-100">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
        {activities.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No activities</h3>
            <p className="mt-1 text-sm text-gray-500">No recent system activities to display</p>
          </div>
        ) : (
          activities.map((activity) => (
            <div key={activity.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-start space-x-3">
                {/* Activity Icon */}
                <div className="flex-shrink-0 pt-0.5">
                  {getActivityIcon(activity.action_type)}
                </div>

                {/* Activity Details */}
                <div className="flex-1 min-w-0">
                  {/* Company Name Badge */}
                  {activity.company_name && (
                    <div className="mb-1">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                        {activity.company_name}
                      </span>
                    </div>
                  )}

                  {/* User and Action */}
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-sm font-medium text-gray-900">
                      {activity.user_first_name} {activity.user_last_name}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getActionTypeColor(activity.action_type)}`}>
                      {activity.action_type.replace(/_/g, ' ')}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-700 mb-1">{activity.description}</p>

                  {/* Project Name (if applicable) */}
                  {activity.project_name && (
                    <p className="text-xs text-gray-500">
                      Project: <span className="font-medium">{activity.project_name}</span>
                      {activity.project_code && ` (${activity.project_code})`}
                    </p>
                  )}
                </div>

                {/* Timestamp */}
                <div className="flex-shrink-0 text-right">
                  <span className="text-xs text-gray-500">
                    {formatRelativeTime(activity.created_at)}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer with last update time */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-center">
        <p className="text-xs text-gray-500">
          Auto-refreshes every 30 seconds
        </p>
      </div>
    </div>
  );
}
